"use strict";

import http from "@/http";
import moment from "moment";
import store from "@/store/store";
import constants from "@/store/modules/constants";

const BASE_NAME = "/contractor-company";

const list = async (filter) => {
  const params = filter ? { filter } : {};
  const response = await http.get(`${BASE_NAME}`, { params });
  return response.data;
};

const getById = async id => {
  const response = await http.get(`${BASE_NAME}/${id}`);
  return response.data;
};

const create = async data => {
  let response = {};
  try {
    response = await http.post(`${BASE_NAME}`, data);
  } catch(error) {
    response.error = error;
  }
  return response;
};

const edit = async contractor => {
  const response = await http.put(`${BASE_NAME}/${contractor._id}`, contractor);
  return response.data;
};

// Guarda una sola nota (obligations | vehicles | employees). Endpoint propio:
// el edit() de arriba manda la contratista entera y pisaria obligaciones,
// vehiculos y personal con la copia que tenga cargada la vista.
const editNotes = async (id, scope, value) => {
  const response = await http.put(`${BASE_NAME}/${id}/notes/${scope}`, {
    value
  });
  return response.data.notes;
};

// Guarda el tamano (px) que el usuario le dio a la caja de notas de una
// pantalla arrastrando la esquina. Devuelve notesSize completo con los
// valores ya topeados por el servidor.
const editNotesSize = async (id, scope, size) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/notes/${scope}/size`,
    { width: size.width, height: size.height }
  );
  return response.data.notesSize;
};

// Guarda un texto de referencia (notas o comentarios) de un empleado o
// vehiculo sin tocar el resto de la contratista. kind: "employees" |
// "vehicles"; field: "notes" | "comments". Devuelve el valor ya recortado
// y topeado por el servidor.
const editReferenceText = async (id, kind, refId, field, value) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/${kind}/${refId}/${field}`,
    { value }
  );
  return response.data.value;
};

const remove = async id => {
  const response = await http.delete(`${BASE_NAME}/${id}`);
  return response.data;
};

const informVisitCancel = async (visitId, state) => {
  const response = await http.post(
    `${BASE_NAME}/${visitId}/visit-state/${state}`
  );
  return response.data;
};

const isValidId = async (id, number) => {
  const response = await http.get(`${BASE_NAME}/${id}/is-valid-id/${number}`);
  return response.data;
};

const isValidEmployeeId = async (id, eid, number) => {
  const response = await http.get(
    `${BASE_NAME}/${id}/employees/${eid}/is-valid-id/${number}`
  );
  return response.data;
};

const lookupIdentifers = async () => {
  const response = await http.get("/lookup-values/identifications");
  return response.data;
};

const search = async filters => {
  const response = await http.post(`${BASE_NAME}/search`, { filters: filters });
  return response.data;
};

const isInvalidObligation = obligation => {
  return (
    [
      store.state.constants.OBLIGATIONS.STATUS.DELAYED,
      store.state.constants.OBLIGATIONS.STATUS.MISSING_DATE
    ].indexOf(obligation.status) != -1
  );
};

const countInvalidObligations = obligations => {
  let count = 0;
  obligations.forEach(obligation => {
    count += !isInvalidObligation(obligation) ? 1 : 0;
  });
  return count;
};

const getFirstInvalidMessage = obligation => {
  if (!obligation.delivered) {
    return store.state.locale.resx.messages.notDelivered;
  }

  if (
    obligation.dueDateType == "date_limit" &&
    moment(obligation.dueDate).isBefore(moment())
  ) {
    return store.state.locale.resx.messages.dueDated;
  }
};

const getObligations = async (id, type, object) => {
  let url;
  if (type == constants.state.OBLIGATIONS.TYPES.VEHICLE_OBLIGATION) {
    url = `${BASE_NAME}/${id}/obligations?type=${type}&vehicle_id=${object.id}&vehicle_type=${object.typeId}`;
  } else if (type == constants.state.OBLIGATIONS.TYPES.EMPLOYEES_OBLIGATION) {
    url = `${BASE_NAME}/${id}/obligations?type=${type}&employee_id=${object.id}`;
  } else if (type == constants.state.OBLIGATIONS.TYPES.OBLIGATION) {
    url = `${BASE_NAME}/${id}/obligations?type=${type}`;
  } else {
    url = `${BASE_NAME}/${id}/obligations`;
  }
  const response = await http.get(url);
  return response.data;
};

const addObligation = async (id, obligation) => {
  const response = await http.post(
    `${BASE_NAME}/${id}/obligations`,
    obligation
  );
  return response.data;
};

const editObligation = async (id, obligation) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/obligations/${obligation._id}`,
    obligation
  );
  return response.data;
};

// Empleados y vehiculos que figuran en una obligacion de nomina.
const setObligationCoverage = async (id, obligation, references) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/obligations/${obligation._id}/coverage`,
    { references }
  );
  return response.data;
};

// Solo las etiquetas de esa obligacion. Va aparte de editObligation() para no
// reenviar la obligacion entera -y no poder pisar campos sin querer- cuando lo
// unico que cambia es el etiquetado. referenceId desempata las obligaciones de
// vehiculo y de personal, que repiten el _id de la maestra.
const setObligationTags = async (id, obligation, tags) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/obligations/${obligation._id}/tags`,
    { tags, referenceId: obligation.referenceId }
  );
  return response.data;
};

// Obligaciones de nomina de las que se excluye a un empleado.
const setEmployeeObligationExclusions = async (id, employeeId, obligations) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/employees/${employeeId}/obligation-exclusions`,
    { obligations }
  );
  return response.data;
};

// El modal "Reporte mensual": etiquetas a informar, observaciones y si la
// planilla muestra solo esas observaciones.
const saveReportSettings = async (id, { tags, comment, notesOnly, previousStatus }) => {
  const response = await http.put(`${BASE_NAME}/${id}/report-settings`, { tags, comment, notesOnly, previousStatus });
  return response.data;
};

// Archivos de una obligacion (spec 2026-09-30). `obligation` viaja entero
// porque referenceId desempata vehiculos y personal, igual que en las etiquetas.
const obligationFilesPath = (id, obligation) =>
  `${BASE_NAME}/${id}/obligations/${obligation._id}/files`;

const uploadObligationFile = async (id, obligation, { file, name, period }) => {
  const form = new FormData();
  form.append("file", file);
  form.append("name", name);
  form.append("period", period);
  if (obligation.referenceId) form.append("referenceId", obligation.referenceId);
  const response = await http.post(obligationFilesPath(id, obligation), form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

const listObligationFiles = async (id, obligation) => {
  const params = obligation.referenceId ? { referenceId: obligation.referenceId } : {};
  const response = await http.get(obligationFilesPath(id, obligation), { params });
  return response.data;
};

const obligationFilesSummary = async id => {
  const response = await http.get(`${BASE_NAME}/${id}/obligation-files/summary`);
  return response.data;
};

// Blob y no URL: una pestaña nueva no lleva el header de autorizacion, asi que
// el archivo se baja con la sesion y se abre como blob: (spec 2.2).
const downloadObligationFile = async (id, fileId) => {
  const response = await http.get(`${BASE_NAME}/${id}/obligation-files/${fileId}`, {
    responseType: "blob"
  });
  return response.data;
};

const deleteObligationFile = async (id, fileId) => {
  await http.delete(`${BASE_NAME}/${id}/obligation-files/${fileId}`);
};

const deleteObligation = async (id, obligation) => {
  const response = await http.delete(
    `${BASE_NAME}/${id}/obligations/${obligation._id}`
  );
  return response.data;
};

const obligationAuth = async (id, oid, action) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/obligations/${oid}/authorization`,
    { action }
  );
  return response.data;
};

const getVehicles = async id => {
  const response = await http.get(`${BASE_NAME}/${id}/vehicles`);
  return response.data;
};

const addVehicle = async (id, vehicle) => {
  const response = await http.post(`${BASE_NAME}/${id}/vehicles`, vehicle);
  return response.data;
};

const editVehicle = async (id, vehicle) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/vehicles/${vehicle._id}`,
    vehicle
  );
  return response.data;
};

const deleteVehicle = async (id, vehicle) => {
  const response = await http.delete(
    `${BASE_NAME}/${id}/vehicles/${vehicle._id}`
  );
  return response.data;
};

// Excluir / habilitar un vehiculo para el analisis de aptitud. Devuelve la
// contratista, como el resto de los endpoints de vehiculos.
const setVehicleExclusion = async (id, vehicleId, isExcluded) => {
  const response = await http.put(
    `${BASE_NAME}/${id}/vehicles/${vehicleId}/exclusion`,
    { isExcluded }
  );
  return response.data;
};

const addVisit = async (id, visit) => {
  const response = await http.post(`${BASE_NAME}/${id}/visit/${visit}`, visit);
  return response.data;
};

const haveVisitToday = async id => {
  const response = await http.get(`${BASE_NAME}/${id}/visit/today`);
  return !response.data;
};

const rateContractor = async (
  contractorId,
  visitId,
  rate,
  comment,
  motive,
  user
  ) => {
  const response = await http.put(
    `${BASE_NAME}/${contractorId}/visit/${visitId}/rate`,
    { rate, comment, motive, user }
  );
  return response.data;
};

const getHistory = async contractorId => {
  const response = await http.get(`${BASE_NAME}/${contractorId}/history`);
  return response.data;
};

const isValidEmail = async (contractorId, email) => {
  const response = await http.get(
    `${BASE_NAME}/${contractorId}/is-valid-email/${email}`
  );
  return response.data;
};

const listEmployee = async contractorId => {
  const response = await http.get(`${BASE_NAME}/${contractorId}/employees`);
  return response.data;
};

const createEmployee = async (contractorId, employee) => {
  const response = await http.post(
    `${BASE_NAME}/${contractorId}/employees`,
    employee
  );
  return response.data;
};

const editEmployee = async (contractorId, employee) => {
  const response = await http.put(
    `${BASE_NAME}/${contractorId}/employees/${employee._id}`,
    employee
  );
  return response.data;
};

const deleteEmployee = async (contractorId, employeeId) => {
  const response = await http.delete(
    `${BASE_NAME}/${contractorId}/employees/${employeeId}`
  );
  return response.data;
};

// Excluir / habilitar un empleado para el analisis de aptitud. Devuelve la
// lista de personal, como el resto de los endpoints de personal.
const setEmployeeExclusion = async (contractorId, employeeId, isExcluded) => {
  const response = await http.put(
    `${BASE_NAME}/${contractorId}/employees/${employeeId}/exclusion`,
    { isExcluded }
  );
  return response.data;
};

const createRequestAccess = async (contractorId, employeesPPI) => {
  const response = await http.post(
    `${BASE_NAME}/${contractorId}/request-access`,
    employeesPPI
  );
  return response.data;
};

const getRequestAccess = async contractorId => {
  const response = await http.get(
    `${BASE_NAME}/${contractorId}/request-access`
  );
  return response.data;
};

const approveRequestAccess = async (
  contractorId,
  requestAccessId,
  action,
  nid
) => {
  const response = await http.put(
    `${BASE_NAME}/${contractorId}/request-access/${requestAccessId}/${action}/${nid}`
  );
  return response.data;
};

const allowVisitRequestAccess = async (
  contractorId,
  requestAccessId,
  visitParams,
  state
) => {
  const response = await http.post(
    `${BASE_NAME}/${contractorId}/request-access/${requestAccessId}/createVisit/${JSON.stringify(
      visitParams
    )}/${state}`
  );
  return response.data;
};

const addVisitWithoutRequestAccess = async (id, state, visitParams) => {
  const response = await http.post(
    `${BASE_NAME}/${id}/visit/${state}/${JSON.stringify(visitParams)}`
  );
  return response.data;
};

const getVisitForRequestAccess = async requestAccessId => {
  const response = await http.get(`${BASE_NAME}/visit/${requestAccessId}`);
  return response.data;
};

const updateVisit = async (vid, state, rid) => {
  const response = await http.post(
    `${BASE_NAME}/visit-update/${vid}/${state}/${rid}`
  );
  return response.data;
};

const getVisitors = async visitId => {
  const response = await http.get(`${BASE_NAME}/visit/${visitId}/visitors`);
  return response.data;
};

const updateVisitorState = async (visitId, visitorId, state) => {
  const response = await http.post(
    `${BASE_NAME}/visit/${visitId}/visitor/${visitorId}/${state}`
  );
  return response.data;
};

const getSWO = async () => {
  const response = await http.post(`${BASE_NAME}/swo`);
  return response.data;
};

export default {
  list,
  search,
  getById,
  create,
  edit,
  editNotes,
  editNotesSize,
  editReferenceText,
  remove,
  isValidId,
  informVisitCancel,
  isValidEmployeeId,
  lookupIdentifers,
  isInvalidObligation,
  countInvalidObligations,
  getFirstInvalidMessage,
  rateContractor,
  isValidEmail,
  getSWO,

  // Obligations
  getObligations,
  addObligation,
  editObligation,
  setObligationTags,
  saveReportSettings,
  setEmployeeObligationExclusions,
  setObligationCoverage,
  uploadObligationFile,
  listObligationFiles,
  obligationFilesSummary,
  downloadObligationFile,
  deleteObligationFile,
  deleteObligation,
  obligationAuth,

  // Vehicles
  getVehicles,
  addVehicle,
  editVehicle,
  deleteVehicle,
  setVehicleExclusion,

  // Visits
  addVisit,
  haveVisitToday,
  getHistory,
  getVisitors,
  updateVisitorState,

  // Employees
  listEmployee,
  createEmployee,
  editEmployee,
  deleteEmployee,
  setEmployeeExclusion,

  // Request Access
  createRequestAccess,
  getRequestAccess,
  approveRequestAccess,
  allowVisitRequestAccess,
  addVisitWithoutRequestAccess,
  getVisitForRequestAccess,
  updateVisit
};
